import { useState } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";

import type { RootState } from "../../../store";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import { useBusiness } from "../../../services/businessService";

const BusinessDetail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isDark = useSelector((state: RootState) => state.theme.darkMode);
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Fetch business from API
  const {
    data: business,
    isLoading,
    error,
  } = useBusiness(id ? parseInt(id) : 0, !!id);

  const gallery =
    business?.gallery || (business?.image ? [business.image] : []);

  if (isLoading) {
    return (
      <div
        className={`min-h-screen ${isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <Header />
        <main className="py-16 px-4 sm:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div
        className={`min-h-screen ${isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <Header />
        <main className="py-16 px-4 sm:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-light mb-4">
              {t("business.businessNotFound")}
            </h1>
            <p
              className={`text-lg mb-8 ${isDark ? "text-gray-400" : "text-gray-600"
                }`}
            >
              {t("business.businessNotFoundText")}
            </p>
            <button
              onClick={() => navigate(`/${currentLanguage}/businesses`)}
              className={`px-6 py-3 rounded-none font-light tracking-wide transition-colors ${isDark
                  ? "bg-gray-700 text-gray-100 hover:bg-gray-600"
                  : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
            >
              {t("business.backToBusinesses")}
            </button>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % gallery.length);
  };
  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + gallery.length) % gallery.length
    );
  };

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
        }`}
    >
      <Header />

      {/* Image Gallery Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-screen p-4">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 z-10 text-white hover:text-gray-300 transition-colors text-2xl"
            >
              ×
            </button>
            {gallery.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
                >
                  ‹
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
                >
                  ›
                </button>
              </>
            )}
            <img
              src={selectedImage}
              alt={business.name}
              className="max-w-full max-h-full object-contain"
            />
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 text-white text-sm">
              {currentImageIndex + 1} / {gallery.length}
            </div>
          </div>
        </div>
      )}

      <main>
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative h-96 md:h-[60vh] overflow-hidden"
        >
          <img
            src={business.image || "https://via.placeholder.com/800x400"}
            alt={business.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="max-w-4xl"
            >
              <div className="flex items-center space-x-4 mb-4">
                {business.category && (
                  <span
                    className={`px-3 py-1 text-xs font-medium tracking-wide ${isDark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-white/90 text-gray-900"
                      }`}
                  >
                    {business.category}
                  </span>
                )}
                {business.verified && (
                  <div className="flex items-center space-x-1 text-green-400">
                    <span className="text-sm">✓</span>
                    <span className="text-sm font-medium">
                      {t("business.verified")}
                    </span>
                  </div>
                )}
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                {business.name || "Unnamed Business"}
              </h1>
              {business.description && (
                <p className="text-lg md:text-xl text-white/90 font-light leading-relaxed max-w-2xl">
                  {business.description}
                </p>
              )}
              <div className="flex items-center space-x-6 mt-6">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={`text-lg ${i < Math.floor(business.rating || 0)
                          ? "text-yellow-400"
                          : "text-gray-400"
                        }`}
                    >
                      ★
                    </span>
                  ))}
                  <span className="text-white ml-2">
                    {business.rating?.toFixed(1) || "0.0"} (
                    {business.reviews || 0} {t("business.reviews")})
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Business Details */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* About */}
                <div
                  className={`p-8 rounded-2xl shadow-xl ${isDark ? "bg-gray-800" : "bg-white"
                    }`}
                >
                  <h2 className="text-3xl font-bold mb-6 text-emerald-600 dark:text-emerald-400">
                    {t("business.about")}
                  </h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        {t("business.businessInfo")}
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            {t("business.owner")}:
                          </span>
                          <span>{business.owner || "N/A"}</span>
                        </div>
                        {business.established && (
                          <div className="flex items-center space-x-3">
                            <span className="font-medium text-gray-600 dark:text-gray-400">
                              {t("business.established")}:
                            </span>
                            <span>{business.established}</span>
                          </div>
                        )}
                        <div className="flex items-start space-x-3">
                          <span className="text-gray-600 dark:text-gray-400 mt-0.5">
                            📍
                          </span>
                          <span>{business.location || "N/A"}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        {t("business.contact")}
                      </h3>
                      <div className="space-y-3">
                        {business.contact?.phone && (
                          <div className="flex items-center space-x-3">
                            <span className="text-gray-600 dark:text-gray-400">
                              📞
                            </span>
                            <span>{business.contact.phone}</span>
                          </div>
                        )}
                        {business.contact?.email && (
                          <div className="flex items-center space-x-3">
                            <span className="text-gray-600 dark:text-gray-400">
                              ✉️
                            </span>
                            <span>{business.contact.email}</span>
                          </div>
                        )}
                        {business.contact?.website && (
                          <div className="flex items-center space-x-3">
                            <span className="text-gray-600 dark:text-gray-400">
                              🌐
                            </span>
                            <a
                              href={business.contact.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                            >
                              {business.contact.website}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div
                  className={`p-8 rounded-2xl shadow-xl ${isDark ? "bg-gray-800" : "bg-white"
                    }`}
                >
                  <h3 className="text-2xl font-bold mb-6 text-emerald-600 dark:text-emerald-400">
                    {t("business.services")}
                  </h3>
                  {business.services && business.services.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      {business.services.map((service, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-3"
                        >
                          <span className="text-green-500 flex-shrink-0">
                            ✓
                          </span>
                          <span>{service}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 dark:text-gray-400">
                      {t("business.noServices", "No services listed")}
                    </p>
                  )}
                </div>

                {/* Features */}
                <div
                  className={`p-8 rounded-2xl shadow-xl ${isDark ? "bg-gray-800" : "bg-white"
                    }`}
                >
                  <h3 className="text-2xl font-bold mb-6 text-stone-600 dark:text-stone-400">
                    {t("business.features")}
                  </h3>
                  {business.features && business.features.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      {business.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-3"
                        >
                          <div className="w-2 h-2 bg-stone-500 rounded-full flex-shrink-0"></div>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 dark:text-gray-400">
                      {t("business.noFeatures", "No features listed")}
                    </p>
                  )}
                </div>

                {/* Hours */}
                <div
                  className={`p-8 rounded-2xl shadow-xl ${isDark ? "bg-gray-800" : "bg-white"
                    }`}
                >
                  <h3 className="text-2xl font-bold mb-6 text-emerald-600 dark:text-emerald-400">
                    <span className="inline mr-2">🕐</span>
                    {t("business.hours")}
                  </h3>
                  {business.hours && Object.keys(business.hours).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(business.hours).map(([day, hours]) => (
                        <div key={day} className="flex justify-between py-1">
                          <span className="capitalize font-medium">{day}:</span>
                          <span>{hours || "Closed"}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 dark:text-gray-400">
                      {t("business.noHours", "No hours listed")}
                    </p>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Gallery */}
                {gallery.length > 0 && (
                  <div
                    className={`p-6 rounded-2xl shadow-xl ${isDark ? "bg-gray-800" : "bg-white"
                      }`}
                  >
                    <h3 className="text-xl font-bold mb-4 text-stone-600 dark:text-stone-400">
                      {t("business.gallery")}
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {gallery.slice(0, 4).map((image, index) => (
                        <img
                          key={index}
                          src={image}
                          alt={`${business.name} ${index + 1}`}
                          className="w-full h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setSelectedImage(image);
                            setCurrentImageIndex(index);
                          }}
                        />
                      ))}
                    </div>
                    {gallery.length > 4 && (
                      <button
                        onClick={() => setSelectedImage(gallery[0])}
                        className="w-full mt-4 py-2 bg-stone-600 hover:bg-stone-700 text-white rounded-lg transition-colors"
                      >
                        {t("business.viewAllPhotos")}
                      </button>
                    )}
                  </div>
                )}

                {/* Social Media */}
                {business.socialMedia && (
                  <div
                    className={`p-6 rounded-2xl shadow-xl ${isDark ? "bg-gray-800" : "bg-white"
                      }`}
                  >
                    <h3 className="text-xl font-bold mb-4 text-emerald-600 dark:text-emerald-400">
                      {t("business.followUs")}
                    </h3>
                    <div className="space-y-3">
                      {business.socialMedia.facebook && (
                        <a
                          href={business.socialMedia.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-3 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                        >
                          <span>Facebook</span>
                        </a>
                      )}
                      {business.socialMedia.instagram && (
                        <a
                          href={business.socialMedia.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-3 text-stone-600 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-300"
                        >
                          <span>Instagram</span>
                        </a>
                      )}
                      {business.socialMedia.twitter && (
                        <a
                          href={business.socialMedia.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-3 text-emerald-400 hover:text-emerald-600 dark:text-emerald-300 dark:hover:text-emerald-500"
                        >
                          <span>Twitter</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Rating & Reviews */}
                <div
                  className={`p-6 rounded-2xl shadow-xl ${isDark ? "bg-gray-800" : "bg-white"
                    }`}
                >
                  <h3 className="text-xl font-bold mb-4 text-yellow-600 dark:text-yellow-400">
                    {t("business.customerReviews")}
                  </h3>
                  <div className="flex items-center space-x-2 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <span
                        key={i}
                        className={`text-lg ${i < Math.floor(business.rating || 0)
                            ? "text-yellow-400"
                            : "text-gray-300"
                          }`}
                      >
                        ★
                      </span>
                    ))}
                    <span className="text-lg font-semibold">
                      {business.rating?.toFixed(1) || "0.0"}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("business.basedOn")} {business.reviews || 0}{" "}
                    {t("business.reviews")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16 bg-gradient-to-r from-emerald-600 to-stone-600 text-white"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t("business.visitUs")}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {t("business.experienceQuality")}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-bold text-lg shadow-lg hover:bg-gray-50 transition-colors">
                {t("business.getDirections")}
              </button>
              <button
                onClick={() => navigate(`/${currentLanguage}/businesses`)}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-emerald-600 transition-colors"
              >
                {t("business.exploreMore")}
              </button>
            </div>
          </div>
        </motion.section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default BusinessDetail;
